/**
 * Akan (Twi) catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 3.
 *
 * `dir: 'ltr'`. Akan/Twi at a mathematician's register. **Warrant → «Nnyinasoɔ»**
 * (basis/justification — flagged). Scientific loanwords are used where Akan standardly borrows
 * (logic → Lɔ́jik, modal, heuristic, scalar, geometry). W/M grades stay Latin
 * (`::numbering-system/latn`). CLDR cardinal categories are one / other (`one` covers 0–1); the
 * noun uses the ɔ-/a- pattern (ɔhye → ahye). `frameActive` declares one/other plus an explicit
 * `=0`. Several abstract terms are flagged "expert-review suggested." Per INV-STABLE-ID a wrong
 * translation is a presentation concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const AK_CATALOG = {
    locale: 'ak',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Nhwehwɛmu',
        [P2.sectionClaims]: 'Nsɛm a wɔka',
        [P2.sectionIntegrity]: 'Nokwaredi',
        [P2.axisWarrantLabel]: 'Nnyinasoɔ',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Nsɔhwɛ',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Nkyerɛaseɛ} interp {Asekyerɛ} testable {Wobetumi asɔ ahwɛ} tested {Wɔasɔ ahwɛ} refuted {Wɔapo} program {Nhwehwɛmu nhyehyɛeɛ} philos {Nyansapɛ} other {Wonnim}}',
        [P2.axisModalLabel]: 'Modal',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Ahoɔden seseɛ {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Wobetumi ahwehwɛ mu',
        [P2.stateRefuted]: 'Wɔapo',
        [P2.stateOutside]: 'Ɛkwan no akyi',
        [P2.verdictConflict]: 'Ɛne {constraints} di abira',
        [P2.verdictOutside]: 'Ɛhia {missing}',
        [P2.lossRegisterPlain]: 'Wɔayɛ no mmerɛ wɔ kasa kɛkɛ mu — wɔagyae pɛpɛɛpɛ no bi.',
        [P2.lossRegisterKid]: 'Wɔayɛ no mmerɛ ma akenkanfoɔ nkumaa — wɔagyae nsɛm pii.',
        [P2.lossProfileDetail]: 'Profaeli a edi mũ a ɛyɛ Nnyinasoɔ / Nsɔhwɛ / Modal no, wɔaka ho asɛm tiawa wɔ ha.',
        [P2.lossConflictDetail]: 'Wɔatwa akasakasa no ho nsɛm tiawa wɔ saa nhwɛsoɔ yi mu.',
        [P2.laneVerified]: 'Wɔahwɛ ahu',
        [P2.laneUnverified]: 'Wɔnhwɛ nhuiɛ',
        [P2.laneConflict]: 'Wɔada akasakasa adi',
        [P2.constraintLogic]: 'Lɔ́jik',
        [P2.constraintLightBends]: 'Hann no kyea wɔ asuon ahoɔden afuo mu',
        [P2.constraintParallelPostulate]: 'Ntwamutoɔ a ɛsesɛ no postulate',
        [P2.claimScalarGravity]: 'Asuon ahoɔden scalar nkyerɛkyerɛ',
        [P2.claimNonEuclidean]: 'Geometri a ɛnyɛ Euclid deɛ',
        [P2.claimSubstrateSr]: 'Substrate asekyerɛ a ɛfa abusuabɔ titiriw ho',
        [P2.edgeExtObservation]: 'Akyire nhwɛ',
        [P2.edgeResourceSample]: 'Akontabuo adeɛ a wɔkyɛ',
        [P2.edgeHeuristicSample]: 'Heuristic akwankyerɛ',
        [P2.edgeSharedSample]: 'Fapem a wɔkyɛ',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Ahye',
        [P2.sectionEdges]: 'Mfianoma',
        [P2.sectionFrame]: 'Fremu',
        [P2.sectionExposure]: 'Adieyi',
        [P2.statusAdoptedFormal]: 'Wɔagye atom (kwan pa so)',
        [P2.statusAdoptedInterpretive]: 'Wɔagye atom (asekyerɛ kwan so)',
        [P2.statusSuspended]: 'Wɔagyae',
        [P2.edgeEffectConstraint]: 'Ɛyɛ ɔhye (ɛbetumi apo)',
        [P2.edgeEffectResource]: 'Adeɛ — ɛnnya atemmuo so nkɛntɛnso biara',
        [P2.edgeEffectHeuristic]: 'Heuristic — ɛnnya atemmuo so nkɛntɛnso biara',
        [P2.edgeEffectShared]: 'Fapem a wɔkyɛ — ɛnnya atemmuo so nkɛntɛnso biara',
        [P2.frameActive]: '{count, plural, =0 {Ahye a ɛyɛ adwuma biara nni hɔ} one {# ɔhye a ɛyɛ adwuma} other {# ahye a ɛyɛ adwuma}}',
        [P2.exposureNone]: 'Asekyerɛ ahiadeɛ biara nni hɔ',
        [P2.exposureDep]: 'Ɛgyina {id} so',
        [P2.laneTrackMachine]: 'Afidie',
        [P2.laneTrackExpert]: 'Ɔbenfo',
        [P2.laneTrackCommunity]: 'Mpɔtam',
        [P2.expertAudited]: 'Wɔahwɛ mu',
        [P2.expertInProgress]: 'Ɛrekɔ so',
        [P2.expertNotReviewed]: 'Wɔnsan nhwɛɛ mu',
        [P2.communityFlags]: 'Nsɛnkyerɛnneɛ wɔ hɔ',
        [P2.communityNone]: 'Nsɛnkyerɛnneɛ biara nni hɔ',
        [P2.controlChallenge]: 'Po',
        [P2.controlActivate]: 'Hyɛ no dwumadie',
        [P2.controlMakeInterpretive]: 'Yɛ no asekyerɛ deɛ',
        [P2.controlImportEdge]: 'Fa mfianoma bra mu',
        [P2.controlPromote]: 'Ma so',
        [P2.controlUndo]: 'Sane yɛ',
        [P2.controlRedo]: 'San yɛ bio',
        [P2.controlRegister]: 'Kyerɛw din',
        [P2.promoteRejected]: 'Wɔapo ma a wɔde maa so',
        [P2.promoteAccepted]: 'Wɔagye ma a wɔde maa so atom',
        [P2.promoteReasonRefutationTerminal]: 'Adanseɛ ntumi nsa apoɔ yare.',
        [P2.promoteReasonKeystone]: 'Ma a ɛho hia no hia nsɔhwɛ mu mmoa.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Wonsii nsɔhwɛ su no pi.',
        [P2.promoteReasonModalNotFixed]: 'Wonsii modal seseɛ no pi.',
        [P2.promoteReasonWarrantNotImproved]: 'Wɔamma nnyinasoɔ seseɛ no nkɔ so.',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=ak.js.map